// The range a job's dates may fall in: a real date from 2000-01-01 to one year
// ahead. The same rule runs server-side in create-/update-job-opportunity.
// Anything before 2000 predates any application history, and a year below 100
// is what a date field produces when someone edits only the year segment.

import { todayLocalYMD, formatLocalYMD } from './dateFormat'

export const MIN_JOB_DATE = '2000-01-01'

/** Not a real date shown to the user: stored values outside the range render as this. */
export const NO_DATE_LABEL = '—'

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function isRealCalendarDate(ymd: string): boolean {
  const match = YMD.exec(ymd)
  if (!match) return false
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const probe = new Date(y, m - 1, d)
  probe.setFullYear(y)
  return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d
}

/** One year after `today` ("YYYY-MM-DD", local), as "YYYY-MM-DD". */
export function maxJobDate(today: string = todayLocalYMD()): string {
  const [y, m, d] = today.split('-').map(Number)
  const probe = new Date(y + 1, m - 1, d)
  return `${probe.getFullYear()}-${pad(probe.getMonth() + 1)}-${pad(probe.getDate())}`
}

export function isJobDateInRange(value: string, today?: string): boolean {
  if (!isRealCalendarDate(value)) return false
  return value >= MIN_JOB_DATE && value <= maxJobDate(today)
}

/**
 * Field error for a job date input, or null. An empty value is valid: every
 * job date is optional.
 */
export function jobDateError(value: string | null | undefined, today?: string): string | null {
  if (!value) return null
  if (isJobDateInRange(value, today)) return null
  const max = formatLocalYMD(maxJobDate(today), 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `Enter a date between Jan 1, 2000 and ${max}`
}

/** A stored date for display: "—" when absent or outside the range. */
export function formatJobDate(
  value: string | null | undefined,
  locale?: string,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!value || !isJobDateInRange(value)) return NO_DATE_LABEL
  return formatLocalYMD(value, locale, opts)
}
